/**
 * Parseur par descente récursive du sous-ensemble C#/Unity (précédence C# standard).
 *
 * Stratégie d'erreurs :
 * - une VRAIE erreur de syntaxe est fatale (première erreur bloque) → `SimCompileError` ;
 * - une construction HORS PÉRIMÈTRE reconnue (méthode `FixedUpdate`, `foreach`, `List<int>`,
 *   `GetComponent<T>()`…) est signalée puis SAUTÉE pour continuer : on remonte jusqu'à
 *   `MAX_REPORTED_ERRORS` erreurs distinctes en une passe (le parseur s'arrête ensuite).
 */

import type { Token } from './tokens';
import { KEYWORDS } from './tokens';
import { SimCompileError, SimError, syntaxError, unsupportedError } from './errors';
import type {
  AssignOp, BinaryOp, BlockStmt, Declarator, Expr, FieldDecl, LifecycleMethod, MethodDecl, Program, Stmt, TypeRef, VarDeclStmt,
} from './ast';
import { ALLOWED_USINGS, KNOWN_UNSUPPORTED, LIFECYCLE_METHODS } from './whitelist';

export const MAX_REPORTED_ERRORS = 5;

export interface ParseResult {
  program: Program;
  errors: SimError[];
}

/** Signal interne : le plafond d'erreurs remontées est atteint, on rend ce qu'on a. */
class StopParsing extends Error {}

const CLASS_MODIFIERS = new Set(['public', 'private', 'protected', 'internal', 'static', 'abstract', 'sealed', 'partial']);
const MEMBER_MODIFIERS = new Set(['public', 'private', 'protected', 'internal', 'static', 'const', 'readonly', 'override', 'virtual', 'abstract', 'new', 'extern', 'unsafe', 'volatile']);
const NOT_A_TYPE_START = new Set(['if', 'else', 'for', 'foreach', 'while', 'do', 'switch', 'case', 'default', 'break', 'continue', 'return', 'yield', 'goto', 'try', 'catch', 'finally', 'throw', 'new', 'this', 'base', 'null', 'true', 'false', 'lock', 'using', 'is', 'as', 'in', 'out', 'ref', 'typeof', 'checked', 'unchecked']);
const UNSUPPORTED_STATEMENT_KEYWORDS = new Set(['foreach', 'switch', 'do', 'try', 'throw', 'goto', 'lock', 'yield', 'checked', 'unchecked', 'fixed', 'unsafe', 'using']);
const MEMBER_LEVEL_WORDS = new Set(['void', 'public', 'private', 'protected', 'internal', 'class', 'struct', 'enum', 'interface', 'namespace']);
const SIMPLE_TYPE_NAMES = new Set(['float', 'int', 'bool', 'Vector2', 'Vector3', 'double', 'string', 'long', 'short', 'byte', 'char', 'decimal', 'uint', 'object', 'var', 'void']);

export function parse(tokens: Token[]): ParseResult {
  const parser = new Parser(tokens);
  try {
    const program = parser.parseProgram();
    return { program, errors: parser.errors };
  } catch (e) {
    if (e instanceof SimCompileError) {
      return { program: parser.partialProgram(), errors: [...parser.errors, e.error] };
    }
    if (e instanceof StopParsing) {
      return { program: parser.partialProgram(), errors: parser.errors };
    }
    throw e;
  }
}

class Parser {
  readonly errors: SimError[] = [];
  private pos = 0;
  private className = '';
  private readonly fields: FieldDecl[] = [];
  private readonly methods: Partial<Record<LifecycleMethod, MethodDecl>> = {};
  private loopDepth = 0;
  private namespaceDepth = 0;

  constructor(private readonly tokens: Token[]) {}

  partialProgram(): Program {
    return { className: this.className, fields: this.fields, methods: this.methods, usesDeltaTime: false };
  }

  // ------------------------------------------------------------------ helpers

  private peek(offset = 0): Token {
    return this.tokens[Math.min(this.pos + offset, this.tokens.length - 1)];
  }

  private next(): Token {
    const tok = this.tokens[this.pos];
    if (tok.kind !== 'eof') this.pos++;
    return tok;
  }

  private get eof(): boolean {
    return this.peek().kind === 'eof';
  }

  private isWord(value: string, offset = 0): boolean {
    const t = this.peek(offset);
    return t.kind === 'word' && t.value === value;
  }

  private isPunct(value: string, offset = 0): boolean {
    const t = this.peek(offset);
    return t.kind === 'punct' && t.value === value;
  }

  private describe(tok: Token): string {
    switch (tok.kind) {
      case 'eof': return 'la fin du fichier';
      case 'number': return `le nombre \`${tok.value}\``;
      case 'string': return `la chaîne "${tok.value}"`;
      default: return `\`${tok.value}\``;
    }
  }

  /** Position juste après le jeton précédent (là où C# signale un `;` manquant). */
  private afterPrevious(): { line: number; column: number } {
    const prev = this.tokens[this.pos - 1];
    if (!prev) return { line: 1, column: 1 };
    const width = prev.kind === 'string' ? prev.value.length + 2 : prev.value.length;
    return { line: prev.line, column: prev.column + width };
  }

  private fail(err: SimError): never {
    throw new SimCompileError(err);
  }

  private report(err: SimError): void {
    this.errors.push(err);
    if (this.errors.length >= MAX_REPORTED_ERRORS) throw new StopParsing();
  }

  private reportUnsupported(symbol: string, line: number, column: number, fallbackMessage: string, lookupKey: string = symbol): void {
    const known = KNOWN_UNSUPPORTED[lookupKey];
    this.report(unsupportedError(symbol, known?.message ?? fallbackMessage, line, column, known?.hint));
  }

  private expectPunct(value: string, what = ''): Token {
    if (this.isPunct(value)) return this.next();
    const found = this.peek();
    const codes: Record<string, string> = { ';': 'CS1002', ')': 'CS1026', '}': 'CS1513', '{': 'CS1514', '(': 'CS1003', ']': 'CS1003' };
    // Un `;` ou `)` manquant se signale à la fin du jeton précédent (comme le compilateur C#) :
    // c'est la ligne que l'étudiant doit corriger, pas celle du jeton suivant.
    const at = value === ';' || value === ')' ? this.afterPrevious() : { line: found.line, column: found.column };
    this.fail(syntaxError(`Attendu \`${value}\`${what ? ` ${what}` : ''}, trouvé ${this.describe(found)}.`, at.line, at.column, { csharpCode: codes[value] }));
  }

  private expectIdentifier(what = 'un nom'): Token {
    const tok = this.peek();
    if (tok.kind === 'word' && !KEYWORDS.has(tok.value)) return this.next();
    if (tok.kind === 'word') {
      this.fail(syntaxError(`Attendu ${what}, trouvé le mot-clé \`${tok.value}\` (un mot réservé de C# ne peut pas servir de nom).`, tok.line, tok.column, { csharpCode: 'CS1001' }));
    }
    this.fail(syntaxError(`Attendu ${what}, trouvé ${this.describe(tok)}.`, tok.line, tok.column, { csharpCode: 'CS1001' }));
  }

  // ------------------------------------------------------------------ programme

  parseProgram(): Program {
    while (!this.eof) {
      const tok = this.peek();
      if (this.isWord('using')) {
        this.parseUsing();
      } else if (this.isWord('namespace')) {
        this.reportUnsupported('namespace', tok.line, tok.column, 'Pas de `namespace` ici : écris directement la classe.');
        this.next();
        this.expectIdentifier('un nom de namespace');
        while (this.isPunct('.')) { this.next(); this.expectIdentifier(); }
        this.expectPunct('{');
        this.namespaceDepth++;
      } else if (this.isPunct('}') && this.namespaceDepth > 0) {
        this.next();
        this.namespaceDepth--;
      } else if (this.isPunct('[') || (tok.kind === 'word' && (CLASS_MODIFIERS.has(tok.value) || ['class', 'struct', 'interface', 'enum'].includes(tok.value)))) {
        this.parseClass();
      } else {
        this.fail(syntaxError(
          `Attendu \`using UnityEngine;\` ou \`public class Nom : MonoBehaviour\`, trouvé ${this.describe(tok)}. Tout le code doit être à l'intérieur de la classe.`,
          tok.line, tok.column, { csharpCode: 'CS0116' },
        ));
      }
    }
    if (!this.className) {
      this.fail(syntaxError('Aucune classe trouvée : le script doit contenir `public class NomDuScript : MonoBehaviour { … }`.', 1, 1));
    }
    return { className: this.className, fields: this.fields, methods: this.methods, usesDeltaTime: false };
  }

  private parseUsing(): void {
    const start = this.next(); // using
    if (this.isWord('static')) {
      this.reportUnsupported('using static', start.line, start.column, '`using static` n\'est pas au programme de cet exercice.');
      while (!this.eof && !this.isPunct(';')) this.next();
      this.expectPunct(';');
      return;
    }
    let name = this.expectIdentifier('un nom de namespace').value;
    while (this.isPunct('.')) {
      this.next();
      name += '.' + this.expectIdentifier('un nom de namespace').value;
    }
    if (this.isPunct('=')) {
      this.reportUnsupported(`using ${name} =`, start.line, start.column, 'Les alias `using X = …` ne sont pas au programme de cet exercice.');
      while (!this.eof && !this.isPunct(';')) this.next();
    }
    this.expectPunct(';', 'après `using ' + name + '`');
    if (!ALLOWED_USINGS.has(name)) {
      this.reportUnsupported(`using ${name}`, start.line, start.column, `Le namespace \`${name}\` n'est pas au programme de cet exercice : seul \`using UnityEngine;\` est nécessaire.`);
    }
  }

  private parseClass(): void {
    this.skipAttributes();
    while (this.peek().kind === 'word' && CLASS_MODIFIERS.has(this.peek().value)) {
      const mod = this.next();
      if (mod.value === 'static') {
        this.reportUnsupported('static', mod.line, mod.column, '`static` n\'est pas au programme de cet exercice.');
      }
    }
    const kw = this.peek();
    if (kw.kind === 'word' && ['struct', 'interface', 'enum'].includes(kw.value)) {
      this.reportUnsupported(kw.value, kw.line, kw.column, `Déclarer un \`${kw.value}\` n'est pas au programme de cet exercice : une seule classe MonoBehaviour.`);
      this.next();
      this.expectIdentifier('un nom');
      while (!this.eof && !this.isPunct('{')) this.next();
      this.skipBalancedBlock();
      return;
    }
    if (!this.isWord('class')) {
      this.fail(syntaxError(`Attendu \`class\`, trouvé ${this.describe(kw)}.`, kw.line, kw.column, { csharpCode: 'CS1518' }));
    }
    const classTok = this.next();
    const nameTok = this.expectIdentifier('un nom de classe');

    if (this.className) {
      this.reportUnsupported('class', classTok.line, classTok.column, `Une seule classe est acceptée dans cet exercice (\`${this.className}\`) : la classe \`${nameTok.value}\` est hors périmètre.`);
      while (!this.eof && !this.isPunct('{')) this.next();
      this.skipBalancedBlock();
      return;
    }
    this.className = nameTok.value;

    if (this.isPunct(':')) {
      this.next();
      const bases: string[] = [];
      do {
        let base = this.expectIdentifier('un nom de classe de base').value;
        while (this.isPunct('.')) { this.next(); base += '.' + this.expectIdentifier().value; }
        bases.push(base.replace(/^UnityEngine\./, ''));
      } while (this.isPunct(',') && this.next());
      if (!bases.includes('MonoBehaviour')) {
        this.reportUnsupported(`: ${bases.join(', ')}`, nameTok.line, nameTok.column, `La classe doit hériter de \`MonoBehaviour\` pour que Unity appelle \`Start()\` et \`Update()\` — \`${bases.join(', ')}\` est hors périmètre ici.`);
      }
    } else {
      this.report(syntaxError(
        `La classe \`${nameTok.value}\` n'hérite pas de \`MonoBehaviour\` : Unity n'appellerait jamais \`Start()\` ni \`Update()\`.`,
        nameTok.line, nameTok.column,
        { hint: `Écris \`public class ${nameTok.value} : MonoBehaviour\`.` },
      ));
    }

    const open = this.expectPunct('{', `pour ouvrir le corps de la classe \`${nameTok.value}\``);
    while (!this.isPunct('}')) {
      if (this.eof) {
        const at = this.peek();
        this.fail(syntaxError(`Accolade fermante \`}\` manquante : la classe \`${nameTok.value}\` ouverte à la ligne ${open.line} n'est jamais fermée.`, at.line, at.column, { csharpCode: 'CS1513' }));
      }
      this.parseMember();
    }
    this.next(); // }
  }

  // ------------------------------------------------------------------ membres

  private skipAttributes(): { serializeField: boolean } {
    let serializeField = false;
    while (this.isPunct('[')) {
      const open = this.next();
      let depth = 1;
      while (depth > 0) {
        if (this.eof) this.fail(syntaxError(`Attribut \`[…]\` ouvert à la ligne ${open.line} jamais fermé : il manque \`]\`.`, open.line, open.column));
        const t = this.next();
        if (t.kind === 'punct' && t.value === '[') depth++;
        else if (t.kind === 'punct' && t.value === ']') depth--;
        else if (t.kind === 'word' && t.value === 'SerializeField') serializeField = true;
      }
    }
    return { serializeField };
  }

  private parseMember(): void {
    const attrs = this.skipAttributes();
    let isPublic = false;
    while (this.peek().kind === 'word' && MEMBER_MODIFIERS.has(this.peek().value)) {
      // `new` n'est un modificateur qu'au niveau membre suivi d'un type ; ici on l'accepte tel quel.
      const mod = this.next();
      if (mod.value === 'public') isPublic = true;
      else if (mod.value === 'static') this.reportUnsupported('static', mod.line, mod.column, '`static` n\'est pas au programme de cet exercice : tout reste dans l\'instance du MonoBehaviour.');
      else if (mod.value === 'override' || mod.value === 'virtual' || mod.value === 'abstract') {
        this.reportUnsupported(mod.value, mod.line, mod.column, `\`${mod.value}\` n'est pas au programme de cet exercice.`);
      }
    }

    const first = this.peek();
    if (first.kind !== 'word') {
      this.fail(syntaxError(`Attendu un champ (\`float speed = 5f;\`) ou une méthode (\`void Update() { … }\`), trouvé ${this.describe(first)}.`, first.line, first.column, { csharpCode: 'CS1519' }));
    }

    // Constructeur : `Nom() { … }`
    if (first.value === this.className && this.isPunct('(', 1)) {
      this.reportUnsupported(`${first.value}()`, first.line, first.column, 'Un constructeur n\'est pas au programme : dans Unity, l\'initialisation se fait dans `Awake()` ou `Start()`.');
      this.next();
      this.skipParenthesized();
      this.skipMethodBody();
      return;
    }
    if (['class', 'struct', 'enum', 'interface', 'delegate', 'event'].includes(first.value)) {
      const known = first.value === 'class' ? `Une seule classe est acceptée dans cet exercice (\`${this.className}\`).` : `Déclarer un \`${first.value}\` n'est pas au programme de cet exercice.`;
      this.reportUnsupported(first.value, first.line, first.column, known);
      this.next();
      while (!this.eof && !this.isPunct('{') && !this.isPunct(';')) this.next();
      if (this.isPunct('{')) this.skipBalancedBlock(); else this.next();
      return;
    }

    const typeRef = this.parseType();

    if (!(this.peek().kind === 'word')) {
      const found = this.peek();
      if (LIFECYCLE_METHODS.includes(typeRef.text as LifecycleMethod) && this.isPunct('(')) {
        this.fail(syntaxError(`Il manque le type de retour devant \`${typeRef.text}()\` : écris \`void ${typeRef.text}()\`.`, typeRef.line, typeRef.column, { csharpCode: 'CS1520' }));
      }
      this.fail(syntaxError(
        `Attendu un nom de champ ou de méthode après \`${typeRef.text}\`, trouvé ${this.describe(found)}. Le code exécutable doit être à l'intérieur d'une méthode comme \`void Update() { … }\`.`,
        found.line, found.column, { csharpCode: 'CS1519' },
      ));
    }
    const nameTok = this.expectIdentifier('un nom de champ ou de méthode');

    if (this.isPunct('(')) {
      this.parseMethod(typeRef, nameTok);
      return;
    }
    if (this.isPunct('{')) {
      this.reportUnsupported(`${nameTok.value} { get; set; }`, nameTok.line, nameTok.column, 'Les propriétés `{ get; set; }` ne sont pas au programme de cet exercice : utilise un simple champ.');
      this.skipBalancedBlock();
      if (this.isPunct('=')) { while (!this.eof && !this.isPunct(';')) this.next(); this.next(); }
      return;
    }
    if (this.isPunct('=>')) {
      this.reportUnsupported(`${nameTok.value} =>`, nameTok.line, nameTok.column, 'Les membres `=>` (expression-bodied) ne sont pas au programme de cet exercice.');
      while (!this.eof && !this.isPunct(';')) this.next();
      this.expectPunct(';');
      return;
    }
    this.parseFieldDeclarators(typeRef, nameTok, isPublic || attrs.serializeField);
  }

  private parseMethod(typeRef: TypeRef, nameTok: Token): void {
    this.next(); // (
    let hasParams = false;
    if (!this.isPunct(')')) {
      hasParams = true;
      let depth = 1;
      while (depth > 0 && !this.eof) {
        const t = this.next();
        if (t.kind === 'punct' && t.value === '(') depth++;
        else if (t.kind === 'punct' && t.value === ')') depth--;
      }
    } else {
      this.next();
    }
    const name = nameTok.value;

    if (typeRef.text !== 'void') {
      const key = KNOWN_UNSUPPORTED[typeRef.base] ? typeRef.base : '';
      this.reportUnsupported(
        typeRef.text, typeRef.line, typeRef.column,
        `La méthode \`${name}()\` renvoie \`${typeRef.text}\` : les méthodes définies par l'étudiant ne sont pas au programme de cet exercice — tout s'écrit dans \`Update()\` (et \`Start()\` pour l'initialisation).`,
        key || typeRef.text,
      );
      this.skipMethodBody();
      return;
    }
    if (!LIFECYCLE_METHODS.includes(name as LifecycleMethod)) {
      this.reportUnsupported(
        name, nameTok.line, nameTok.column,
        `La méthode \`${name}()\` est définie par toi : les méthodes personnalisées ne sont pas au programme de cet exercice — écris tout dans \`Update()\` (et \`Start()\` pour l'initialisation).`,
        name,
      );
      this.skipMethodBody();
      return;
    }
    if (hasParams) {
      this.reportUnsupported(`${name}(…)`, nameTok.line, nameTok.column, `\`${name}()\` ne prend aucun paramètre dans Unity : retire ce qui est entre les parenthèses.`);
      this.skipMethodBody();
      return;
    }
    const lifecycle = name as LifecycleMethod;
    if (this.methods[lifecycle]) {
      this.fail(syntaxError(`La méthode \`${name}()\` est définie deux fois (la première à la ligne ${this.methods[lifecycle]!.line}).`, nameTok.line, nameTok.column, { csharpCode: 'CS0111' }));
    }
    if (this.isPunct(';')) {
      this.fail(syntaxError(`Le corps de \`${name}()\` est manquant : écris \`void ${name}() { … }\`.`, nameTok.line, nameTok.column, { csharpCode: 'CS0501' }));
    }
    const body = this.parseBlock(`de \`${name}()\``);
    this.methods[lifecycle] = { name: lifecycle, body, line: nameTok.line, column: nameTok.column };
  }

  private skipMethodBody(): void {
    if (this.isPunct('{')) {
      this.skipBalancedBlock();
    } else if (this.isPunct('=>')) {
      while (!this.eof && !this.isPunct(';')) this.next();
      this.next();
    } else if (this.isPunct(';')) {
      this.next();
    }
  }

  private skipParenthesized(): void {
    this.expectPunct('(');
    let depth = 1;
    while (depth > 0 && !this.eof) {
      const t = this.next();
      if (t.kind === 'punct' && t.value === '(') depth++;
      else if (t.kind === 'punct' && t.value === ')') depth--;
    }
  }

  private skipBalancedBlock(): void {
    const open = this.expectPunct('{');
    let depth = 1;
    while (depth > 0) {
      if (this.eof) {
        const at = this.peek();
        this.fail(syntaxError(`Accolade fermante \`}\` manquante : le bloc ouvert à la ligne ${open.line} n'est jamais fermé.`, at.line, at.column, { csharpCode: 'CS1513' }));
      }
      const t = this.next();
      if (t.kind === 'punct' && t.value === '{') depth++;
      else if (t.kind === 'punct' && t.value === '}') depth--;
    }
  }

  private parseFieldDeclarators(typeRef: TypeRef, firstName: Token, serialized: boolean): void {
    let nameTok = firstName;
    for (;;) {
      let init: Expr | undefined;
      if (this.isPunct('=')) {
        this.next();
        init = this.parseExpression();
      }
      this.fields.push({ typeRef, name: nameTok.value, init, serialized, line: nameTok.line, column: nameTok.column });
      if (this.isPunct(',')) {
        this.next();
        nameTok = this.expectIdentifier('un nom de champ');
        continue;
      }
      break;
    }
    this.expectPunct(';', `après la déclaration de \`${nameTok.value}\``);
  }

  // ------------------------------------------------------------------ types

  private parseType(): TypeRef {
    const tok = this.peek();
    if (tok.kind !== 'word' || NOT_A_TYPE_START.has(tok.value)) {
      this.fail(syntaxError(`Attendu un type (\`float\`, \`int\`, \`bool\`, \`Vector2\`, \`Vector3\`), trouvé ${this.describe(tok)}.`, tok.line, tok.column, { csharpCode: 'CS1031' }));
    }
    this.next();
    let text = tok.value;
    while (this.isPunct('.') && this.peek(1).kind === 'word') {
      this.next();
      text += '.' + this.next().value;
    }
    text = text.replace(/^UnityEngine\./, '').replace(/^System\.Collections\.Generic\./, '').replace(/^System\./, '');
    const base = text;
    let generic = false;
    let array = false;

    if (this.isPunct('<')) {
      generic = true;
      this.next();
      const inner: string[] = [];
      if (!this.isPunct('>')) {
        do {
          inner.push(this.parseTypeQuiet().text);
        } while (this.isPunct(',') && this.next());
      }
      this.expectPunct('>', `pour fermer \`${base}<…\``);
      text = `${base}<${inner.join(', ')}>`;
    }
    if (this.isPunct('[') && this.isPunct(']', 1)) {
      this.next();
      this.next();
      array = true;
      text += '[]';
    }
    if (this.isPunct('?')) {
      this.next();
      this.reportUnsupported(`${text}?`, tok.line, tok.column, 'Les types nullables (`float?`) ne sont pas au programme de cet exercice.');
    }

    if (base === 'var') {
      this.reportUnsupported('var', tok.line, tok.column, '`var` est refusé ici : écris le type explicitement.');
    } else if (generic) {
      this.reportUnsupported(text, tok.line, tok.column, `Le type générique \`${text}\` n'est pas au programme de cet exercice.`, base);
    } else if (array) {
      this.reportUnsupported(text, tok.line, tok.column, `Les tableaux (\`${text}\`) ne sont pas au programme de cet exercice : seuls \`float\`, \`int\`, \`bool\`, \`Vector2\`, \`Vector3\` sont disponibles.`);
    }
    return { text, base, generic, array, line: tok.line, column: tok.column };
  }

  /** Type imbriqué dans un générique : on ne signale rien (le générique parent l'est déjà). */
  private parseTypeQuiet(): TypeRef {
    const saved = this.errors.length;
    const ref = this.parseType();
    this.errors.length = saved;
    return ref;
  }

  // ------------------------------------------------------------------ instructions

  private parseBlock(what = ''): BlockStmt {
    const open = this.expectPunct('{', what ? `pour ouvrir le bloc ${what}` : 'pour ouvrir un bloc');
    const body: Stmt[] = [];
    while (!this.isPunct('}')) {
      if (this.eof) {
        const at = this.peek();
        this.fail(syntaxError(`Accolade fermante \`}\` manquante : le bloc${what ? ' ' + what : ''} ouvert à la ligne ${open.line} n'est jamais fermé.`, at.line, at.column, { csharpCode: 'CS1513' }));
      }
      body.push(this.parseStatement());
    }
    this.next();
    return { kind: 'block', body, line: open.line, column: open.column };
  }

  private parseStatement(): Stmt {
    const tok = this.peek();
    if (this.isPunct('{')) return this.parseBlock();
    if (this.isPunct(';')) {
      this.next();
      return { kind: 'empty', line: tok.line, column: tok.column };
    }

    if (tok.kind === 'word') {
      switch (tok.value) {
        case 'if': return this.parseIf();
        case 'for': return this.parseFor();
        case 'while': return this.parseWhile();
        case 'return': {
          this.next();
          if (!this.isPunct(';')) {
            this.fail(syntaxError('`Start()` et `Update()` ne renvoient rien (`void`) : écris `return;` sans valeur, ou retire ce `return`.', tok.line, tok.column, { csharpCode: 'CS0127' }));
          }
          this.next();
          return { kind: 'return', line: tok.line, column: tok.column };
        }
        case 'break':
        case 'continue': {
          this.next();
          if (this.loopDepth === 0) {
            this.fail(syntaxError(`\`${tok.value}\` ne peut s'utiliser qu'à l'intérieur d'une boucle (\`for\` / \`while\`).`, tok.line, tok.column, { csharpCode: 'CS0139' }));
          }
          this.expectPunct(';', `après \`${tok.value}\``);
          return { kind: tok.value, line: tok.line, column: tok.column };
        }
        case 'else':
          this.fail(syntaxError('`else` sans `if` correspondant : vérifie les accolades du `if` précédent.', tok.line, tok.column, { csharpCode: 'CS8641' }));
          break;
        case 'case':
        case 'default':
          this.fail(syntaxError(`\`${tok.value}\` n'a de sens que dans un \`switch\`, qui n'est pas au programme ici.`, tok.line, tok.column));
          break;
        default:
          break;
      }
      if (UNSUPPORTED_STATEMENT_KEYWORDS.has(tok.value)) {
        this.reportUnsupported(tok.value, tok.line, tok.column, `\`${tok.value}\` n'est pas au programme de cet exercice.`);
        this.next();
        this.skipStatement();
        return { kind: 'error', line: tok.line, column: tok.column };
      }
      if (MEMBER_LEVEL_WORDS.has(tok.value)) {
        this.fail(syntaxError(
          `\`${tok.value}\` inattendu à l'intérieur d'une méthode : on ne peut pas déclarer une méthode ou une classe dans une autre — il manque sans doute une accolade \`}\` avant cette ligne.`,
          tok.line, tok.column, { csharpCode: 'CS1513' },
        ));
      }
      if (this.isDeclarationStart()) {
        const decl = this.parseVarDecl();
        this.expectPunct(';', `après la déclaration de \`${decl.declarators[decl.declarators.length - 1].name}\``);
        return decl;
      }
    }

    const expr = this.parseExpression();
    this.validateStatementExpression(expr);
    this.expectPunct(';', 'à la fin de l\'instruction');
    return { kind: 'expr', expr, line: tok.line, column: tok.column };
  }

  private validateStatementExpression(expr: Expr): void {
    if (expr.kind === 'assign' || expr.kind === 'update' || expr.kind === 'call' || expr.kind === 'error') return;
    if (expr.kind === 'binary' && expr.op === '==') {
      this.fail(syntaxError(
        'Cette ligne compare (`==`) mais ne fait rien du résultat. Tu voulais sans doute une affectation : un seul `=`.',
        expr.line, expr.column, { csharpCode: 'CS0201' },
      ));
    }
    this.fail(syntaxError(
      'Cette expression ne fait rien : seules une affectation (`x = …`), un appel (`transform.Translate(…)`) ou une incrémentation (`x++`) peuvent former une instruction.',
      expr.line, expr.column, { csharpCode: 'CS0201' },
    ));
  }

  /** `Type nom` (avec génériques / tableaux) en tête d'instruction ? */
  private isDeclarationStart(): boolean {
    const t0 = this.peek();
    if (t0.kind !== 'word' || NOT_A_TYPE_START.has(t0.value)) return false;
    if (t0.value === 'var') return true;
    let k = 1;
    while (this.isPunct('.', k) && this.peek(k + 1).kind === 'word') k += 2;
    if (this.isPunct('<', k)) {
      let depth = 0;
      for (;;) {
        const t = this.peek(k);
        if (t.kind === 'eof') return false;
        if (t.kind === 'punct' && t.value === '<') depth++;
        else if (t.kind === 'punct' && t.value === '>') { depth--; if (depth === 0) { k++; break; } }
        else if (t.kind === 'word' || (t.kind === 'punct' && (t.value === ',' || t.value === '.' || t.value === '[' || t.value === ']'))) { /* ok */ }
        else return false;
        k++;
      }
    }
    if (this.isPunct('[', k) && this.isPunct(']', k + 1)) k += 2;
    const name = this.peek(k);
    return name.kind === 'word' && !KEYWORDS.has(name.value);
  }

  private parseVarDecl(): VarDeclStmt {
    const typeRef = this.parseType();
    const declarators: Declarator[] = [];
    for (;;) {
      const nameTok = this.expectIdentifier('un nom de variable');
      let init: Expr | undefined;
      if (this.isPunct('=')) {
        this.next();
        init = this.parseExpression();
      }
      declarators.push({ name: nameTok.value, init, line: nameTok.line, column: nameTok.column });
      if (this.isPunct(',')) { this.next(); continue; }
      break;
    }
    return { kind: 'vardecl', typeRef, declarators, line: typeRef.line, column: typeRef.column };
  }

  private parseIf(): Stmt {
    const tok = this.next(); // if
    this.expectPunct('(', 'après `if`');
    const test = this.parseExpression();
    this.expectPunct(')', 'pour fermer la condition du `if`');
    const consequent = this.parseStatement();
    let alternate: Stmt | undefined;
    if (this.isWord('else')) {
      this.next();
      alternate = this.parseStatement();
    }
    return { kind: 'if', test, consequent, alternate, line: tok.line, column: tok.column };
  }

  private parseFor(): Stmt {
    const tok = this.next(); // for
    this.expectPunct('(', 'après `for`');
    let init: Stmt | undefined;
    if (!this.isPunct(';')) {
      if (this.isDeclarationStart()) {
        init = this.parseVarDecl();
      } else {
        const expr = this.parseExpression();
        this.validateStatementExpression(expr);
        init = { kind: 'expr', expr, line: expr.line, column: expr.column };
      }
    }
    this.expectPunct(';', 'après l\'initialisation du `for`');
    let test: Expr | undefined;
    if (!this.isPunct(';')) test = this.parseExpression();
    this.expectPunct(';', 'après la condition du `for`');
    const update: Expr[] = [];
    if (!this.isPunct(')')) {
      do {
        const expr = this.parseExpression();
        this.validateStatementExpression(expr);
        update.push(expr);
      } while (this.isPunct(',') && this.next());
    }
    this.expectPunct(')', 'pour fermer l\'en-tête du `for`');
    this.loopDepth++;
    const body = this.parseStatement();
    this.loopDepth--;
    return { kind: 'for', init, test, update, body, line: tok.line, column: tok.column };
  }

  private parseWhile(): Stmt {
    const tok = this.next(); // while
    this.expectPunct('(', 'après `while`');
    const test = this.parseExpression();
    this.expectPunct(')', 'pour fermer la condition du `while`');
    this.loopDepth++;
    const body = this.parseStatement();
    this.loopDepth--;
    return { kind: 'while', test, body, line: tok.line, column: tok.column };
  }

  /** Saute une instruction hors périmètre : jusqu'au `;` de même niveau ou la fin de son bloc `{ … }`. */
  private skipStatement(): void {
    let depth = 0;
    for (;;) {
      const t = this.peek();
      if (t.kind === 'eof') return;
      if (t.kind === 'punct' && t.value === '}' && depth === 0) return;
      this.next();
      if (t.kind === 'punct') {
        if (t.value === '{') depth++;
        else if (t.value === '}') { depth--; if (depth === 0) return; }
        else if (t.value === ';' && depth === 0) return;
      }
    }
  }

  // ------------------------------------------------------------------ expressions

  parseExpression(): Expr {
    return this.parseAssignment();
  }

  private parseAssignment(): Expr {
    const left = this.parseConditional();
    const tok = this.peek();
    if (tok.kind === 'punct' && ['=', '+=', '-=', '*=', '/=', '%='].includes(tok.value)) {
      if (left.kind !== 'ident' && left.kind !== 'member' && left.kind !== 'error') {
        this.fail(syntaxError('La partie gauche d\'une affectation doit être une variable (ou `transform.position`), pas une expression.', left.line, left.column, { csharpCode: 'CS0131' }));
      }
      this.next();
      const value = this.parseAssignment();
      return { kind: 'assign', op: tok.value as AssignOp, target: left, value, line: left.line, column: left.column };
    }
    return left;
  }

  private parseConditional(): Expr {
    const test = this.parseOr();
    if (this.isPunct('?')) {
      this.next();
      const consequent = this.parseAssignment();
      this.expectPunct(':', 'dans l\'expression conditionnelle `? :`');
      const alternate = this.parseAssignment();
      return { kind: 'conditional', test, consequent, alternate, line: test.line, column: test.column };
    }
    return test;
  }

  private parseBinaryLevel(ops: readonly BinaryOp[], nextLevel: () => Expr): Expr {
    let left = nextLevel();
    for (;;) {
      const tok = this.peek();
      if (tok.kind === 'punct' && (ops as readonly string[]).includes(tok.value)) {
        this.next();
        const right = nextLevel();
        left = { kind: 'binary', op: tok.value as BinaryOp, left, right, line: left.line, column: left.column };
        continue;
      }
      return left;
    }
  }

  private parseOr(): Expr { return this.parseBinaryLevel(['||'], () => this.parseAnd()); }
  private parseAnd(): Expr { return this.parseBinaryLevel(['&&'], () => this.parseEquality()); }
  private parseEquality(): Expr { return this.parseBinaryLevel(['==', '!='], () => this.parseRelational()); }
  private parseRelational(): Expr { return this.parseBinaryLevel(['<', '>', '<=', '>='], () => this.parseAdditive()); }
  private parseAdditive(): Expr { return this.parseBinaryLevel(['+', '-'], () => this.parseMultiplicative()); }
  private parseMultiplicative(): Expr { return this.parseBinaryLevel(['*', '/', '%'], () => this.parseUnary()); }

  private parseUnary(): Expr {
    const tok = this.peek();
    if (tok.kind === 'punct') {
      if (tok.value === '-' || tok.value === '+' || tok.value === '!') {
        this.next();
        const operand = this.parseUnary();
        return { kind: 'unary', op: tok.value, operand, line: tok.line, column: tok.column };
      }
      if (tok.value === '++' || tok.value === '--') {
        this.next();
        const target = this.parseUnary();
        this.checkUpdateTarget(target);
        return { kind: 'update', op: tok.value, prefix: true, target, line: tok.line, column: tok.column };
      }
      // Cast `(int)x` / `(float)x`
      if (tok.value === '(' && this.peek(1).kind === 'word' && this.isPunct(')', 2) && SIMPLE_TYPE_NAMES.has(this.peek(1).value)) {
        const typeTok = this.peek(1);
        if (typeTok.value === 'int' || typeTok.value === 'float') {
          this.next(); this.next(); this.next();
          const expr = this.parseUnary();
          return { kind: 'cast', typeName: typeTok.value, expr, line: tok.line, column: tok.column };
        }
        this.reportUnsupported(`(${typeTok.value})`, tok.line, tok.column, `La conversion \`(${typeTok.value})\` n'est pas au programme : seuls \`(int)\` et \`(float)\` sont acceptés.`, typeTok.value);
        this.next(); this.next(); this.next();
        this.parseUnary();
        return { kind: 'error', line: tok.line, column: tok.column };
      }
      if (tok.value === '&' || tok.value === '*' || tok.value === '~') {
        this.fail(syntaxError(`Opérateur \`${tok.value}\` inattendu au début d'une expression.`, tok.line, tok.column, { csharpCode: 'CS1525' }));
      }
    }
    return this.parsePostfix();
  }

  private checkUpdateTarget(target: Expr): void {
    if (target.kind !== 'ident' && target.kind !== 'member' && target.kind !== 'error') {
      this.fail(syntaxError('`++` / `--` ne s\'appliquent qu\'à une variable.', target.line, target.column, { csharpCode: 'CS1059' }));
    }
  }

  private parsePostfix(): Expr {
    let expr = this.parsePrimary();
    for (;;) {
      const tok = this.peek();
      if (tok.kind !== 'punct') return expr;
      if (tok.value === '.') {
        this.next();
        const name = this.expectIdentifier('un nom de membre après `.`');
        expr = { kind: 'member', object: expr, name: name.value, line: expr.line, column: expr.column };
        continue;
      }
      if (tok.value === '(') {
        const args = this.parseArguments();
        expr = { kind: 'call', callee: expr, args, line: expr.line, column: expr.column };
        continue;
      }
      if (tok.value === '++' || tok.value === '--') {
        this.next();
        this.checkUpdateTarget(expr);
        expr = { kind: 'update', op: tok.value, prefix: false, target: expr, line: expr.line, column: expr.column };
        continue;
      }
      if (tok.value === '[') {
        this.reportUnsupported('[…]', tok.line, tok.column, 'L\'indexation `[…]` (tableaux, listes) n\'est pas au programme de cet exercice.');
        let depth = 0;
        do {
          const t = this.next();
          if (t.kind === 'punct' && t.value === '[') depth++;
          else if (t.kind === 'punct' && t.value === ']') depth--;
          if (t.kind === 'eof') break;
        } while (depth > 0);
        expr = { kind: 'error', line: expr.line, column: expr.column };
        continue;
      }
      return expr;
    }
  }

  private parseArguments(): Expr[] {
    const open = this.expectPunct('(');
    const args: Expr[] = [];
    if (this.isPunct(')')) {
      this.next();
      return args;
    }
    for (;;) {
      const t = this.peek();
      if (t.kind === 'word' && (t.value === 'out' || t.value === 'ref')) {
        this.reportUnsupported(t.value, t.line, t.column, `Les paramètres \`${t.value}\` ne sont pas au programme de cet exercice.`);
        this.next();
      }
      args.push(this.parseExpression());
      if (this.isPunct(',')) { this.next(); continue; }
      break;
    }
    this.expectPunct(')', `pour fermer la parenthèse ouverte ligne ${open.line}`);
    return args;
  }

  private parsePrimary(): Expr {
    const tok = this.peek();
    switch (tok.kind) {
      case 'number': {
        this.next();
        return { kind: 'literal', literalType: tok.numberKind ?? 'int', value: tok.numberValue ?? 0, raw: tok.value, line: tok.line, column: tok.column };
      }
      case 'string': {
        this.next();
        return { kind: 'literal', literalType: 'string', value: tok.value, raw: `"${tok.value}"`, line: tok.line, column: tok.column };
      }
      case 'punct': {
        if (tok.value === '(') {
          this.next();
          const inner = this.parseExpression();
          this.expectPunct(')', `pour fermer la parenthèse ouverte ligne ${tok.line}`);
          return inner;
        }
        if (tok.value === '$' && this.peek(1).kind === 'string') {
          this.reportUnsupported('$"…"', tok.line, tok.column, 'L\'interpolation de chaîne `$"…"` n\'est pas au programme : concatène avec `+` dans `Debug.Log("x = " + x)`.');
          this.next(); this.next();
          return { kind: 'error', line: tok.line, column: tok.column };
        }
        if (tok.value === '@' && this.peek(1).kind === 'string') {
          this.reportUnsupported('@"…"', tok.line, tok.column, 'Les chaînes verbatim `@"…"` ne sont pas au programme de cet exercice.');
          this.next(); this.next();
          return { kind: 'error', line: tok.line, column: tok.column };
        }
        if (tok.value === ')' || tok.value === ';' || tok.value === '}' || tok.value === ',') {
          this.fail(syntaxError(`Expression attendue, trouvé ${this.describe(tok)} : il manque une valeur avant.`, tok.line, tok.column, { csharpCode: 'CS1525' }));
        }
        this.fail(syntaxError(`\`${tok.value}\` inattendu dans une expression.`, tok.line, tok.column, { csharpCode: 'CS1525' }));
        break;
      }
      case 'eof':
        this.fail(syntaxError('Expression attendue, mais le fichier se termine ici.', tok.line, tok.column, { csharpCode: 'CS1525' }));
        break;
      case 'word':
        return this.parseWordPrimary(tok);
      default:
        break;
    }
    this.fail(syntaxError(`Jeton inattendu ${this.describe(tok)}.`, tok.line, tok.column));
  }

  private parseWordPrimary(tok: Token): Expr {
    switch (tok.value) {
      case 'true':
      case 'false':
        this.next();
        return { kind: 'literal', literalType: 'bool', value: tok.value === 'true', raw: tok.value, line: tok.line, column: tok.column };
      case 'null':
        this.reportUnsupported('null', tok.line, tok.column, '`null` n\'a pas sa place ici : `float`, `int`, `bool`, `Vector2`, `Vector3` sont des types valeur, jamais nuls.');
        this.next();
        return { kind: 'error', line: tok.line, column: tok.column };
      case 'new':
        return this.parseNew();
      case 'this': {
        this.next();
        this.expectPunct('.', 'après `this`');
        const name = this.expectIdentifier('un nom de membre après `this.`');
        return { kind: 'ident', name: name.value, line: tok.line, column: tok.column };
      }
      case 'base':
        this.reportUnsupported('base', tok.line, tok.column, '`base` n\'est pas au programme de cet exercice.');
        this.next();
        return { kind: 'error', line: tok.line, column: tok.column };
      case 'typeof':
      case 'is':
      case 'as':
        this.reportUnsupported(tok.value, tok.line, tok.column, `\`${tok.value}\` n'est pas au programme de cet exercice.`);
        this.next();
        return { kind: 'error', line: tok.line, column: tok.column };
      default:
        break;
    }
    if (KEYWORDS.has(tok.value)) {
      this.fail(syntaxError(`Mot-clé \`${tok.value}\` inattendu dans une expression.`, tok.line, tok.column, { csharpCode: 'CS1525' }));
    }

    // Appel générique `GetComponent<Rigidbody>()` : `Ident < Ident(.Ident)* > (`
    if (this.isPunct('<', 1) && this.peek(2).kind === 'word') {
      let k = 2;
      let inner = this.peek(k).value;
      k++;
      while (this.isPunct('.', k) && this.peek(k + 1).kind === 'word') { inner += '.' + this.peek(k + 1).value; k += 2; }
      if (this.isPunct('>', k) && this.isPunct('(', k + 1)) {
        const symbol = `${tok.value}<${inner}>`;
        this.reportUnsupported(symbol, tok.line, tok.column, `\`${symbol}\` n'est pas au programme de cet exercice.`, tok.value);
        for (let n = 0; n <= k; n++) this.next();
        this.parseArguments();
        return { kind: 'error', line: tok.line, column: tok.column };
      }
    }

    this.next();
    return { kind: 'ident', name: tok.value, line: tok.line, column: tok.column };
  }

  private parseNew(): Expr {
    const tok = this.next(); // new
    const typeTok = this.peek();
    if (typeTok.kind !== 'word') {
      this.fail(syntaxError(`Attendu un type après \`new\`, trouvé ${this.describe(typeTok)}.`, typeTok.line, typeTok.column, { csharpCode: 'CS1031' }));
    }
    this.next();
    let typeName = typeTok.value;
    while (this.isPunct('.') && this.peek(1).kind === 'word') { this.next(); typeName += '.' + this.next().value; }
    typeName = typeName.replace(/^UnityEngine\./, '');
    if (this.isPunct('<') || this.isPunct('[')) {
      const symbol = `new ${typeName}${this.isPunct('<') ? '<…>' : '[…]'}`;
      this.reportUnsupported(symbol, tok.line, tok.column, `\`${symbol}\` n'est pas au programme de cet exercice.`, typeName);
      // Saute jusqu'au `;` de l'instruction (ou la fin de la parenthèse englobante)
      let depth = 0;
      while (!this.eof) {
        const t = this.peek();
        if (t.kind === 'punct' && (t.value === ';' || (t.value === ')' && depth === 0) || (t.value === ',' && depth === 0))) break;
        this.next();
        if (t.kind === 'punct' && (t.value === '(' || t.value === '[' || t.value === '{' || t.value === '<')) depth++;
        else if (t.kind === 'punct' && (t.value === ')' || t.value === ']' || t.value === '}' || t.value === '>')) depth--;
      }
      return { kind: 'error', line: tok.line, column: tok.column };
    }
    const args = this.parseArguments();
    if (this.isPunct('{')) {
      this.reportUnsupported(`new ${typeName} { … }`, tok.line, tok.column, 'Les initialiseurs d\'objet `new X { … }` ne sont pas au programme de cet exercice.');
      this.skipBalancedBlock();
      return { kind: 'error', line: tok.line, column: tok.column };
    }
    return { kind: 'new', typeName, args, line: tok.line, column: tok.column };
  }
}
